import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { SectionHeading } from '../components/shared/SectionHeading';

const PrivacyPage: React.FC = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`privacyPage.sections.${key}`);
  const items = (key: string) => t(`privacyPage.sections.${key}`, { returnObjects: true }) as string[];
  const rows = t('privacyPage.sections.purposes.rows', { returnObjects: true }) as string[][];

  return (
    <div className="page-shell">
      <Header />

      <section className="section-shell">
        <div className="section-container">
          <div className="surface-card-soft p-8 md:p-10">
            <Link to="/" className="mb-6 inline-block text-sm text-slate-500 hover:text-primary hover:underline">
              {t('privacyPage.backToHome')}
            </Link>
            <SectionHeading
              eyebrow={t('privacyPage.title')}
              title={t('privacyPage.title')}
              subtitle={t('privacyPage.lastUpdated')}
            />
          </div>
        </div>
      </section>

      <div className="section-container pb-20">
        <div className="mx-auto max-w-4xl space-y-6">
        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('controller.title')}</h2>
          <p className="leading-relaxed text-slate-700">{s('controller.text')}</p>
          <div className="mt-4 rounded-2xl border border-primary/10 bg-primary/5 p-4 text-sm text-slate-700 space-y-1">
            <p><strong>{s('controller.name')}</strong> Inburgertest</p>
            <p><strong>{s('controller.location')}</strong> Amsterdam, Nederland</p>
            <p><strong>{s('controller.email')}</strong> <a href="mailto:privacy@inburgertest.nl" className="text-primary underline">privacy@inburgertest.nl</a></p>
            <p><strong>{s('controller.kvk')}</strong> {s('controller.kvkValue')}</p>
          </div>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('dataCollected.title')}</h2>
          <ul className="list-disc space-y-2 pl-6 text-slate-700">
            {(items('dataCollected.items') || []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('purposes.title')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-primary/10">
                  <th className="rounded-tl-lg p-3 text-left font-semibold">{s('purposes.purpose')}</th>
                  <th className="rounded-tr-lg p-3 text-left font-semibold">{s('purposes.legalBasis')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary/10">
                {Array.isArray(rows) && rows.map((row, i) => (
                  <tr key={i}>
                    <td className="p-3 text-slate-700">{row[0]}</td>
                    <td className="p-3 text-slate-600">{row[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('retention.title')}</h2>
          <ul className="list-disc space-y-2 pl-6 text-slate-700">
            {(items('retention.items') || []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('thirdParties.title')}</h2>
          <p className="mb-3 leading-relaxed text-slate-700">{s('thirdParties.text')}</p>
          <ul className="list-disc space-y-2 pl-6 text-slate-700">
            {(items('thirdParties.items') || []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <p className="mt-3 leading-relaxed text-slate-700">{s('thirdParties.noSelling')}</p>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('openai.title')}</h2>
          <p className="leading-relaxed text-slate-700">{s('openai.text')}</p>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('rights.title')}</h2>
          <p className="mb-3 leading-relaxed text-slate-700">{s('rights.text')}</p>
          <ul className="list-disc space-y-2 pl-6 text-slate-700">
            {(items('rights.items') || []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <p className="mt-4 leading-relaxed text-slate-700">{s('rights.contact')}</p>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('security.title')}</h2>
          <p className="leading-relaxed text-slate-700">{s('security.text')}</p>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('breaches.title')}</h2>
          <p className="leading-relaxed text-slate-700">{s('breaches.text')}</p>
          <ul className="mt-2 list-disc space-y-2 pl-6 text-slate-700">
            {(items('breaches.items') || []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <p className="mt-3 leading-relaxed text-slate-700">{s('breaches.note')}</p>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('cookies.title')}</h2>
          <p className="leading-relaxed text-slate-700">
            {s('cookies.text').split(s('cookies.link'))[0]}
            <Link to="/cookies" className="text-primary underline">{s('cookies.link')}</Link>
            {s('cookies.text').split(s('cookies.link'))[1] || ''}
          </p>
        </section>

        <section className="surface-card p-6 md:p-8">
          <h2 className="mb-4 text-2xl font-bold text-ink">{s('changes.title')}</h2>
          <p className="leading-relaxed text-slate-700">{s('changes.text')}</p>
        </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
